import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";

import { loadScriptOnce } from "./loadScriptOnce";
import { updateViewer } from "../../reducers/viewer";
import { useRef } from "react";
import { sleep } from "./sleep";

export const sendToMatomo = (eventName, ...eventData) => {
  window._paq = window._paq || [];
  window._paq.push([eventName, ...eventData]);
};

// Track the current URL's page view.
const trackPageView = async () => {
  const { location } = window;
  const url = location?.pathname + location?.search + location?.hash;

  // We wait 500ms and check back if the URL is still the same, avoiding to track a page
  // view on a page that's redirected immediately.
  await sleep(500);

  const { location: newLocation } = window;
  const newUrl = newLocation?.pathname + newLocation?.search + newLocation?.hash;

  if (newUrl !== url) return;

  sendToMatomo("trackPageView", url);
};

// This hook is called from the `RoutesContext` component, which must be a descendant of
// the Redux & React Router context providers to work.
//
export const useMatomoTracking = analytics => {
  const { url: analyticsURL, identifier: siteId } = analytics || {};

  const [matomoLoaded, setMatomoLoaded] = useState(false);
  const dispatch = useDispatch();

  const isEnabled = useSelector(({ viewer }) => viewer?.acceptsCookies);

  useEffect(() => {
    if (!isEnabled || !analyticsURL || matomoLoaded) return;

    // Set the current platform associated Matomo site id so the URLs are tracked on the
    // right Matomo site.
    sendToMatomo("setSiteId", siteId);
    // Use Matomo's hosted tracking backend
    sendToMatomo("setTrackerUrl", `${analyticsURL}/matomo.php`);
    // Enable external link click tracking
    sendToMatomo("enableLinkTracking");
    // Define visitor cookie timeout to 6 months (in seconds)
    sendToMatomo("setVisitorCookieTimeout", 15778476);

    // Load the Matomo tracking script after the configuration is done. Once the script is
    // loaded, the tracking will start immediately thanks to the below useEffect.
    loadScriptOnce(
      `${analyticsURL}/matomo.js`,
      () => {
        setMatomoLoaded(true);

        // Mark the viewer as tracked
        dispatch(updateViewer({ isTracked: true }));
      },
      // Let the script loading fail silently, as browser extensions or ad blockers might
      // block it, and we can't do anything about it.
      { silent: true }
    );
  }, [analyticsURL, dispatch, isEnabled, matomoLoaded, siteId]);

  const location = useLocation();
  const previousLocation = useRef(null);

  // Whenever the matomo script is loaded, track the current page view and all subsequent
  // location changes.
  useEffect(() => {
    // If matomo is not loaded, or tracking is disabled we don't do anything.
    if (!matomoLoaded) return;

    // Ensure that there's no double tracking of the same page, because of the user
    // navigating to the same page.
    if (previousLocation.current !== location.pathname) {
      trackPageView();

      previousLocation.current = location.pathname;
    }
  }, [location, matomoLoaded]);
};
